import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import {
	pauseSubscription,
	SubscriptionActionTypes,
	useBusinessLayer,
	useDTicketSubscription,
} from '@mopla/business-logic';
import {
	calculateSubscriptionPauseDates,
	ISubscriptionPauseDates,
	TSubscriptionPauseDuration,
} from '@mopla/utils';

import { usePassengerAppNavigate } from '../../../../hooks';

interface IPendingPauseData {
	duration: TSubscriptionPauseDuration;
	dates: ISubscriptionPauseDates;
}

interface IPendingCancelData {
	hasPendingCancel: boolean;
	cancelDate: string;
}

export const useDTicketSubscriptionPause = () => {
	const { navigateToUserDTicket } = usePassengerAppNavigate();
	const bl = useBusinessLayer();
	const { subscription, isSubscriptionLoading } = useDTicketSubscription();

	const pendingCancelData: IPendingCancelData = useMemo(() => {
		const hasPendingCancel = !!subscription?.canceledAt;
		const cancelDate = subscription?.canceledAt
			? dayjs(subscription.canceledAt).format('DD.MM.YYYY')
			: '';
		return { hasPendingCancel, cancelDate };
	}, [subscription?.canceledAt]);

	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	const [isPauseLoading, setIsPauseLoading] = useState(false);
	const [pendingPauseData, setPendingPauseData] =
		useState<IPendingPauseData | null>(null);

	// Cancellation and pause effective dates can be used interchangeably
	// TODO: DTicket 2.0 – rename when BE switches to a generic name
	const nextCancellationEffectiveDate =
		subscription?.nextCancellationEffectiveDate
			? subscription.nextCancellationEffectiveDate
			: null;

	const initiatePause = useCallback(
		(duration: TSubscriptionPauseDuration) => {
			if (!nextCancellationEffectiveDate) return;

			const dates = calculateSubscriptionPauseDates(
				parseInt(duration, 10),
				nextCancellationEffectiveDate
			);

			setPendingPauseData({ duration, dates });
			setIsConfirmationModalOpen(true);
		},
		[nextCancellationEffectiveDate]
	);

	const confirmPause = useCallback(() => {
		if (!pendingPauseData || !subscription?.id) return;

		setIsPauseLoading(true);

		const pauseStartFormatted = dayjs(
			pendingPauseData.dates.pauseStart,
			'DD.MM.YYYY'
		).format('YYYY-MM-DD');
		const pauseEndFormatted = dayjs(
			pendingPauseData.dates.pauseStart,
			'DD.MM.YYYY'
		)
			.add(parseInt(pendingPauseData.duration, 10) - 1, 'month')
			.endOf('month')
			.format('YYYY-MM-DD');

		bl.dispatch(
			pauseSubscription({
				subscriptionId: subscription.id,
				pauseStart: pauseStartFormatted,
				pauseEnd: pauseEndFormatted,
			})
		);

		bl.watchActions({
			types: SubscriptionActionTypes.PauseSubscriptionResult,
			callback: (action) => {
				setIsPauseLoading(false);
				setIsConfirmationModalOpen(false);

				if (action.payload?.error) {
					console.error('Error pausing subscription:', action.payload.error);
					setIsErrorModalOpen(true);
				} else {
					setIsSuccessModalOpen(true);
				}
			},
		});
	}, [pendingPauseData, subscription?.id, bl]);

	const cancelConfirmation = useCallback(() => {
		setIsConfirmationModalOpen(false);
		setPendingPauseData(null);
		navigateToUserDTicket();
	}, [navigateToUserDTicket]);

	const closeSuccessModal = useCallback(() => {
		setIsSuccessModalOpen(false);
		setPendingPauseData(null);
		navigateToUserDTicket();
	}, [navigateToUserDTicket]);

	const retryPause = useCallback(() => {
		setIsErrorModalOpen(false);
		setIsConfirmationModalOpen(false);
	}, []);

	return {
		isSubscriptionLoading,
		pendingPauseData,
		pendingCancelData,
		isConfirmationModalOpen,
		isSuccessModalOpen,
		isErrorModalOpen,
		isPauseLoading,
		initiatePause,
		confirmPause,
		cancelConfirmation,
		closeSuccessModal,
		retryPause,
	};
};
